import React, { useCallback, useEffect, useRef, useState } from "react";
import "./ScoreProgress.scss";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import Loader from "../../../atoms/Loader";
import Spinner from "../../../atoms/Spinner/Spinner";
import { useScoreProgress } from "../../../hooks/useScoreProgress";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import {
  SCORE_PROGRESS_MESSAGES,
  SCORE_PROGRESS_SCROLL,
  SCORE_PROGRESS_SELECTORS,
} from "./constants";

const { END_THRESHOLD: SCROLL_END_THRESHOLD } = SCORE_PROGRESS_SCROLL;

const ScoreProgress = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const scoringHistory = useSelector(
    (state: RootState) => state.scoring.scoringHistory
  );

  const {
    isGetScoringHistoryLoading,
    loadMoreData,
    totalScores,
    hasMore,
  } = useScoreProgress();

  const getScrollStep = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return 0;

    const card = container.querySelector<HTMLElement>(
      SCORE_PROGRESS_SELECTORS.PROGRESS_CARD
    );
    if (!card) return container.clientWidth;

    const cardWidth = card.getBoundingClientRect().width;
    const arrow = container.querySelector<HTMLElement>(
      SCORE_PROGRESS_SELECTORS.ARROW
    );
    const arrowWidth = arrow?.getBoundingClientRect().width ?? 0;
    const gap =
      parseFloat(getComputedStyle(container).gap) ||
      parseFloat(getComputedStyle(container).columnGap) ||
      0;

    return cardWidth + arrowWidth + gap;
  }, []);

  const updateScrollState = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const atEnd =
      container.scrollLeft + container.clientWidth >=
      container.scrollWidth - SCROLL_END_THRESHOLD;

    setCanScrollLeft(container.scrollLeft > SCROLL_END_THRESHOLD);
    setCanScrollRight(!atEnd || hasMore);
  }, [hasMore]);

  useEffect(() => {
    updateScrollState();

    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", updateScrollState);

    return () => {
      container.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [scoringHistory, hasMore, updateScrollState, isExpanded]);

  const handleScrollLeft = () => {
    scrollContainerRef.current?.scrollBy({
      left: -getScrollStep(),
      behavior: "smooth",
    });
  };

  const handleScrollRight = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const atEnd =
      container.scrollLeft + container.clientWidth >=
      container.scrollWidth - SCROLL_END_THRESHOLD;

    if (atEnd && hasMore) {
      loadMoreData();
    } else {
      container.scrollBy({ left: getScrollStep(), behavior: "smooth" });
    }
  };

  const showOverlay =
    isGetScoringHistoryLoading && (scoringHistory?.length ?? 0) > 0;
  const isInitialLoad =
    isGetScoringHistoryLoading && (scoringHistory?.length ?? 0) === 0;
  const loadedCount = scoringHistory?.length ?? 0;

  return (
    <div className="score-progress">
      <div className="header" onClick={() => setIsExpanded(!isExpanded)}>
        <span className={`chevron ${isExpanded ? "expanded" : ""}`}>▼</span>
      </div>

      <div className={`collapsible-content ${isExpanded ? "expanded" : ""}`}>
        {loadedCount > 0 && (
          <div className="score-progress-nav">
            <div className="score-progress-nav__meta">
              {totalScores > 0 ? (
                <span>
                  Scores 1–{Math.min(loadedCount, totalScores)} of {totalScores}
                </span>
              ) : (
                <span>{loadedCount} scores loaded</span>
              )}
            </div>

            <div className="score-progress-nav__row">
              <button
                type="button"
                className="score-progress-nav__btn"
                disabled={!canScrollLeft || isGetScoringHistoryLoading}
                onClick={handleScrollLeft}
                aria-label="Scroll to newer scores"
                title="Newer scores"
              >
                <LeftOutlined />
              </button>

              <div
                className={`score-progress-nav__slot${
                  showOverlay ? " score-progress-nav__slot--loading" : ""
                }`}
              >
                <div className="progress-cards" ref={scrollContainerRef}>
                  {scoringHistory.map((card, index) => (
                    <React.Fragment
                      key={`${card.assessment_id}-${card.updated_by?.health_coach_role}`}
                    >
                      <div className="progress-card">
                        <div className="gauge-container">
                          <div className="gauge">
                            <div className="score">{`${
                              card?.score?.metabolism || 0
                            }`}</div>
                          </div>
                        </div>
                        <div className="details">
                          {card?.updated_by?.health_coach_name && (
                            <div className="name">{`${card?.updated_by?.health_coach_name} (${card?.updated_by?.health_coach_role})`}</div>
                          )}
                          <div className="date">
                            {new Date(card.updated_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      {index < scoringHistory.length - 1 && (
                        <div className="arrow">˿</div>
                      )}
                    </React.Fragment>
                  ))}
                </div>

                {showOverlay && (
                  <div className="score-progress-nav__overlay">
                    <Spinner size="medium" color="#299D6B" />
                  </div>
                )}
              </div>

              <button
                type="button"
                className="score-progress-nav__btn"
                disabled={!canScrollRight || isGetScoringHistoryLoading}
                onClick={handleScrollRight}
                aria-label="Scroll to older scores"
                title="Older scores"
              >
                <RightOutlined />
              </button>
            </div>
          </div>
        )}

        {isInitialLoad && (
          <div className="score-progress-nav__initial-loading">
            <Loader noBackground={true} />
          </div>
        )}

        {!isGetScoringHistoryLoading && loadedCount === 0 && (
          <div className="no-history-message">
            {SCORE_PROGRESS_MESSAGES.NO_HISTORY}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScoreProgress;
